import { useCallback, useEffect, useRef, useState } from "react";
import { APP_DRAFT_STATUS_REFRESH_DELAY_MS, STATUS } from "@/constants";
import {
  discardAppDraft,
  fetchAppDraft,
  openAppDraft,
  publishAppDraft,
  type AppDraftState,
} from "@/features/app-studio/api/appDraftsApi";
import { subscribeToApiMutations } from "@/shared/api/apiMutationEvents";

export type AppDraftStatus = (typeof STATUS)[keyof typeof STATUS];

export type UseAppDraftResult = {
  /** The draft's state; `null` until it loads, and always for an unpublished app. */
  draft: AppDraftState | null;
  status: AppDraftStatus;
  error: string | null;
  /** True while a publish or discard request is in flight. */
  isSaving: boolean;
  /** Re-reads whether the draft differs from what is published. */
  refresh: () => Promise<void>;
  publish: () => Promise<void>;
  discard: () => Promise<void>;
};

/**
 * The draft version App Studio edits a published app through.
 *
 * Enabled only for a published app: it opens the app's draft (creating it on first use), then
 * keeps `has_changes` current by re-checking shortly after any successful write addressed at the
 * draft — whichever dialog, panel or chat action made it.
 */
export function useAppDraft(appId: number | null, enabled: boolean): UseAppDraftResult {
  const [draft, setDraft] = useState<AppDraftState | null>(null);
  const [status, setStatus] = useState<AppDraftStatus>(STATUS.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeAppId = enabled ? appId : null;

  useEffect(() => {
    setDraft(null);
    setError(null);
    if (activeAppId === null) {
      setStatus(STATUS.IDLE);
      return;
    }
    let cancelled = false;
    setStatus(STATUS.LOADING);
    openAppDraft(activeAppId)
      .then((state) => {
        if (!cancelled) {
          setDraft(state);
          setStatus(STATUS.SUCCEEDED);
        }
      })
      .catch((openError: unknown) => {
        if (!cancelled) {
          setError(openError instanceof Error ? openError.message : null);
          setStatus(STATUS.FAILED);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeAppId]);

  const refresh = useCallback(async () => {
    if (activeAppId === null) {
      return;
    }
    try {
      setDraft(await fetchAppDraft(activeAppId));
    } catch {
      // A failed re-check leaves the last known state; the next write tries again.
    }
  }, [activeAppId]);

  const draftAppId = draft?.draft_app_id ?? null;

  useEffect(() => {
    if (draftAppId === null) {
      return;
    }
    const draftPathPrefix = `/v1/apps/${draftAppId}/`;
    const unsubscribe = subscribeToApiMutations((path) => {
      if (!path.startsWith(draftPathPrefix)) {
        return;
      }
      // Debounced: one edit is often several writes (a widget and its new query, a reorder).
      if (refreshTimer.current !== null) {
        clearTimeout(refreshTimer.current);
      }
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = null;
        void refresh();
      }, APP_DRAFT_STATUS_REFRESH_DELAY_MS);
    });
    return () => {
      unsubscribe();
      if (refreshTimer.current !== null) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [draftAppId, refresh]);

  const runDraftAction = useCallback(
    async (action: (appId: number) => Promise<AppDraftState>) => {
      if (activeAppId === null) {
        return;
      }
      setIsSaving(true);
      setError(null);
      try {
        setDraft(await action(activeAppId));
      } catch (actionError) {
        setError(actionError instanceof Error ? actionError.message : null);
        throw actionError;
      } finally {
        setIsSaving(false);
      }
    },
    [activeAppId],
  );

  const publish = useCallback(() => runDraftAction(publishAppDraft), [runDraftAction]);
  const discard = useCallback(() => runDraftAction(discardAppDraft), [runDraftAction]);

  return { draft, status, error, isSaving, refresh, publish, discard };
}
